import { z } from 'zod';

/**
 * API-key limits (operator, 2026-09-26: "this limits are per org" — then "go
 * ahead, use 10 per minute for olt writes"). The shape is the one integrations
 * written against the compatibility API already expect, so a script that lived
 * inside those limits lives inside these.
 *
 * Three layers, and a call counts against every one that applies to it:
 *
 *  1. EVERY API-key call, per ORGANISATION — all of an org's keys share one
 *     budget, because what it protects (this server, the org's OLTs and
 *     routers) is shared by all of them and a key is free to mint. 1,000 an
 *     hour, plus a burst rule: 10 a second sustained, blocked above 15 a
 *     second.
 *  2. What the call COSTS here. Live OLT reads and OLT writes are budgeted PER
 *     OLT — the OLT's management plane is the scarce thing, shared with this
 *     platform's own sweeps and every open ONU page — the rest per org. And
 *     they are budgeted by HOW the OLT is reached: an operation the OLT
 *     answers over SNMP costs it tens of milliseconds, over a CLI session
 *     seconds, so SNMP gets its own, far larger, budgets (operator,
 *     2026-09-26: "as we keep building and finding better, faster, easier ways
 *     of doing some things, lets also adjust (increase) our limits, like we did
 *     when moving from cli to snmp"). A route that moves to a faster path moves
 *     to the lighter budget with it, and the card shows each budget's measured
 *     cost so the numbers can keep following the platform's speed.
 *  3. Per-METHOD caps on the compatibility API, only where the method costs
 *     this platform something of its own: the whole-fleet exports, the GPS
 *     export, the authorisations report, and `get_onu_signal` (a live OLT
 *     read). Every other compatible read is served from stored data and counts
 *     against the hourly budget only.
 *
 * OLT writes (10 a minute per OLT — each takes 3–7 s here, one at a time per
 * OLT, so the OLT's own ceiling is about 8–12 a minute), router actions, line
 * tests and bulk runs have budgets of their own for the same reason: their cost
 * lands on a chassis, a router or this server, not on a database.
 *
 * The card names no other system (operator, 2026-09-26): the numbers stand on
 * what this platform measured, and the `smartolt` figure stays on the DTO for
 * the API reference alone.
 *
 * The shipped mode OBSERVES — counts everything, refuses nothing — until a
 * superadmin enforces the numbers they settle on.
 */

export const API_KEY_LIMIT_BUDGETS = [
  'all',
  'chassis_read',
  'chassis_write',
  'snmp_read',
  'snmp_write',
  'cpe',
  'line_test',
  'bulk',
  'export',
  'read',
  'write',
  'so_all_onus_details',
  'so_all_onus_details_per_olt',
  'so_all_onus_gps',
  'so_onu_signal',
  'so_authorizations_report',
] as const;
export type ApiKeyLimitBudget = (typeof API_KEY_LIMIT_BUDGETS)[number];

/** The cost categories — exactly one per counted call. */
export const API_KEY_COST_CATEGORIES = [
  'chassis_read',
  'chassis_write',
  'snmp_read',
  'snmp_write',
  'cpe',
  'line_test',
  'bulk',
  'export',
  'read',
  'write',
] as const;
export type ApiKeyCostCategory = (typeof API_KEY_COST_CATEGORIES)[number];

/** SmartOLT methods with a cap of their own — at most one per call. */
export const API_KEY_METHOD_BUDGETS = [
  'so_all_onus_details',
  'so_all_onus_details_per_olt',
  'so_all_onus_gps',
  'so_onu_signal',
  'so_authorizations_report',
] as const;
export type ApiKeyMethodBudget = (typeof API_KEY_METHOD_BUDGETS)[number];

/**
 * `off` counts nothing; `observe` counts every key call and records what WOULD
 * have been refused, refusing nothing; `enforce` answers 429 with Retry-After
 * once an organisation (or an OLT) is over a number for the window.
 */
export const API_KEY_LIMIT_MODES = ['off', 'observe', 'enforce'] as const;
export type ApiKeyLimitMode = (typeof API_KEY_LIMIT_MODES)[number];
export const DEFAULT_API_KEY_LIMIT_MODE: ApiKeyLimitMode = 'observe';

export const API_KEY_LIMIT_WINDOW_SECONDS = { minute: 60, tenMinutes: 600, hour: 3600 } as const;
export type ApiKeyLimitWindow = keyof typeof API_KEY_LIMIT_WINDOW_SECONDS;

/** The largest number a limit may be set to — above it, "no limit" is the honest setting. */
export const API_KEY_LIMIT_MAX = 100_000;
/** The largest per-second rate or burst that may be set. */
export const API_KEY_RATE_MAX = 1_000;

export interface ApiKeyLimitBudgetInfo {
  label: string;
  /** Which layer it is — how the card groups it. */
  kind: 'general' | 'cost' | 'method';
  /** Who shares it: every key of an organisation, or every key of an organisation on ONE OLT. */
  scope: 'org' | 'olt';
  /** What counts here, in the operator's words. */
  covers: string;
  /** What one call costs this platform, as measured. */
  measured: string;
  /** Why the suggested number is that number. */
  why: string;
  window: ApiKeyLimitWindow;
  /** The proposal; null = no separate cap (the hourly budget covers it). */
  suggested: number | null;
  /** The compatibility API's published figure for the same thing, where there is one — for the API reference; the card does not show it. */
  smartolt: string | null;
}

export const API_KEY_LIMIT_BUDGET_INFO: Record<ApiKeyLimitBudget, ApiKeyLimitBudgetInfo> = {
  all: {
    label: 'Every API-key call',
    kind: 'general',
    scope: 'org',
    covers: 'Every call any of the organisation’s keys makes, whatever it is — the other budgets come on top of this one.',
    measured:
      'The whole platform served 51,894 requests in two days (about 1,080 an hour, people included); the one integration in daily use makes about 21 an hour.',
    why: 'The budget integrations already expect, per organisation. Enough for integrations that act on events — billing suspends, a CRM checking a signal — and for polling through the bulk calls (all statuses or all signals in one call), never for polling ONUs one at a time.',
    window: 'hour',
    suggested: 1000,
    smartolt: '1,000 an hour, 10 a second, blocked above 15 a second',
  },
  chassis_read: {
    label: 'Live OLT reads (CLI)',
    kind: 'cost',
    scope: 'olt',
    covers:
      "Reads that open a session on the OLT: autofind (unconfigured ONUs), an ONU's live configuration, running config and admin state, optics and traffic refreshes, the OLT clock, probes, syncs, taking a backup, and any OLT read asked for with refresh=1.",
    measured:
      "3 s typical, 17–21 s at p95 (autofind 3 s, a traffic sample 3.5 s, a stale ONU configuration up to 44 s). The OLT's few sessions also serve this platform's sweeps and every open ONU page, which re-reads every 15 s.",
    why: 'Three reads a minute per OLT — 30 every 10 minutes. Per OLT because the cost lands on that OLT, whichever key sends it, and its few CLI sessions also serve this platform’s own sweeps and every open ONU page.',
    window: 'tenMinutes',
    suggested: 30,
    smartolt: '30 every 10 minutes per OLT (cards, PON ports, uplinks)',
  },
  chassis_write: {
    label: 'OLT writes (CLI)',
    kind: 'cost',
    scope: 'olt',
    covers:
      'Anything that changes an OLT: authorise, delete, reboot, resync, recreate, swap, VLANs and ports, management and WAN mode, VoIP, IPTV, security features, uplinks, PON ports, time — and the compatibility API’s single-ONU writes.',
    measured:
      'Each waits behind every other change on that OLT and runs alone: authorise 3 s, resync 7 s, a reboot up to 19 s at p95 — about 8–12 a minute is what one OLT can do in total.',
    why: 'Your number (2026-09-26): 10 a minute per OLT, close to what the OLT can physically take. Every write runs on the OLT before the call answers, so a faster burst would only queue ahead of your operators.',
    window: 'minute',
    suggested: 10,
    smartolt: 'no separate limit (inside 1,000 an hour, up to 10 a second)',
  },
  snmp_read: {
    label: 'OLT reads over SNMP',
    kind: 'cost',
    scope: 'olt',
    covers:
      "Live OLT reads the OLT answers over SNMP instead of a CLI session: an ONU's admin state, an optics refresh on a ZTE C6xx (a C300 still reads its OLT-side receive level over the CLI), a Huawei traffic sample, SNMP walks.",
    measured:
      'An ONU’s admin state read over SNMP: 17 ms typical, 83 ms at p95 — against 3 s for a CLI read. The platform’s own hourly sweep walks thousands of values this way.',
    why: 'One a second per OLT: about a fortieth of a CLI session each, and noise beside the sweep’s own SNMP walks. The CLI budget is three a minute; the same OLT takes twenty times this over SNMP without noticing.',
    window: 'minute',
    suggested: 60,
    smartolt: null,
  },
  snmp_write: {
    label: 'OLT writes over SNMP',
    kind: 'cost',
    scope: 'olt',
    covers:
      'OLT writes that go over SNMP where the OLT has a read-write community: enable/disable an ONU (the compatibility API’s enable and disable included), rename, PON-port and uplink state, and a reboot on a C650.',
    measured:
      'Not in the two-day log (no SNMP write ran in it): a single SNMP set, one request and one answer, with no session to open and no save per write (the six-hourly persist saves them). The measured column fills in as they happen.',
    why: 'One a second per OLT — six times the CLI budget, because the OLT does not have to open a session, run a sequence and save for each one.',
    window: 'minute',
    suggested: 60,
    smartolt: null,
  },
  cpe: {
    label: 'Router (TR-069) actions',
    kind: 'cost',
    scope: 'org',
    covers:
      "Changes and refreshes on a subscriber's router through the ACS: WiFi, WAN, reboot, factory reset, presets, parameters, port forwards, firmware, summon.",
    measured:
      '1.6 s to 17 s each — queued on the ACS and done when the router answers. A router takes one session at a time.',
    why: "Every call is a real session with somebody's router. 30 a minute is a support desk working live.",
    window: 'minute',
    suggested: 30,
    smartolt: 'no separate limit',
  },
  line_test: {
    label: 'Line tests',
    kind: 'cost',
    scope: 'org',
    covers: 'Speed tests, and ping or traceroute diagnostics run from a router or a phone.',
    measured:
      "A download test runs about 12 s and an upload up to 2.5 minutes, pushing real traffic through this server and the subscriber's line.",
    why: 'Each test takes bandwidth this server shares with everything else. 20 an hour is a support desk’s worth, not a monitoring loop.',
    window: 'hour',
    suggested: 20,
    smartolt: null,
  },
  bulk: {
    label: 'Bulk runs',
    kind: 'cost',
    scope: 'org',
    covers:
      'Creating, running and resuming bulk plans, OLT migrations, firmware campaigns, device and link syncs, and the compatibility API’s bulk_* calls.',
    measured:
      'Freezing a plan took up to 5 s at p95; running it is one OLT write per ONU in it. People created 24 plans in two days.',
    why: 'One call here is many OLT writes. Ten an hour is room to work, never a loop — and one bulk call carries a whole list of ONUs.',
    window: 'hour',
    suggested: 10,
    smartolt: 'no separate limit',
  },
  export: {
    label: 'Exports and backups',
    kind: 'cost',
    scope: 'org',
    covers: 'The zones export, OLT configuration backup downloads, and taking a platform backup.',
    measured: 'An OLT backup is its whole configuration, up to about 3 MB; a platform backup is a full database dump.',
    why: 'Fifteen an hour — the rate at which an export stops being a sync and becomes a loop.',
    window: 'hour',
    suggested: 15,
    smartolt: null,
  },
  read: {
    label: 'Other reads',
    kind: 'cost',
    scope: 'org',
    covers:
      'Everything answered from this platform’s own records: ONU and OLT lists and details, statuses and signals as last swept, topology, the action log, a job’s progress — and the compatibility API’s reads other than the capped ones below.',
    measured: '5–70 ms each, p95 under 150 ms.',
    why: 'No separate cap: the hourly budget and the burst rule already cover them.',
    window: 'hour',
    suggested: null,
    smartolt: null,
  },
  write: {
    label: 'Other writes',
    kind: 'cost',
    scope: 'org',
    covers:
      'Changes kept only in this platform: record names and notes, placement, zones and splitters, billing records, presets, notifications.',
    measured: '20–40 ms each.',
    why: 'No separate cap: the hourly budget and the burst rule already cover them.',
    window: 'hour',
    suggested: null,
    smartolt: null,
  },
  so_all_onus_details: {
    label: 'get_all_onus_details (every ONU at once)',
    kind: 'method',
    scope: 'org',
    covers:
      'The compatibility API’s whole-fleet ONU export: get_all_onus_details called without page or olt_id. Paged calls count as ordinary reads.',
    measured: 'One response carries every ONU with about 80 fields each; it grows with the fleet.',
    why: 'One every four minutes is enough for any sync.',
    window: 'hour',
    suggested: 15,
    smartolt: '15 an hour',
  },
  so_all_onus_details_per_olt: {
    label: 'get_all_onus_details_per_olt (one OLT’s ONUs)',
    kind: 'method',
    scope: 'org',
    covers:
      'The same export narrowed to one OLT: get_all_onus_details with olt_id and without page; get_all_onus_details_per_olt/<olt_id> answers — and counts — the same.',
    measured: 'As the whole-fleet export, for one OLT.',
    why: 'The same as the whole-fleet export, so looping over OLTs cannot get around that cap.',
    window: 'hour',
    suggested: 15,
    smartolt: '15 an hour',
  },
  so_all_onus_gps: {
    label: 'get_all_onus_gps_coordinates',
    kind: 'method',
    scope: 'org',
    covers: 'The compatibility API’s export of every ONU’s coordinates.',
    measured: 'Coordinates change only when somebody moves a pin.',
    why: 'Polling coordinates is waste; five an hour is a sync.',
    window: 'hour',
    suggested: 5,
    smartolt: '5 an hour',
  },
  so_onu_signal: {
    label: 'get_onu_signal (read live)',
    kind: 'method',
    scope: 'org',
    covers: 'The compatibility API’s single-ONU signal, read fresh from the OLT.',
    measured: 'One live optics read on the ONU’s OLT.',
    why: 'About 8 a minute is a busy support desk; the bulk signals call is for polling the fleet.',
    window: 'hour',
    suggested: 500,
    smartolt: '500 an hour',
  },
  so_authorizations_report: {
    label: 'get_onus_authorizations_report',
    kind: 'method',
    scope: 'org',
    covers:
      'The ONUs authorised over a period, removed ones included.',
    measured: 'One pass over the organisation’s ONU rows, removed ones included.',
    why: 'The history barely changes; five an hour is a sync.',
    window: 'hour',
    suggested: 5,
    smartolt: '5 an hour',
  },
};

/** The burst rule, per organisation. */
export const API_KEY_RATE_SUGGESTED = { perSecond: 10, burst: 15 } as const;

/** "a minute" / "every 10 minutes" / "an hour" — for the sentences a limit is spoken in. */
export function apiKeyLimitWindowPhrase(window: ApiKeyLimitWindow): string {
  return window === 'hour' ? 'an hour' : window === 'tenMinutes' ? 'every 10 minutes' : 'a minute';
}

const limitValue = z.number().int().min(1).max(API_KEY_LIMIT_MAX).nullable();
const rateValue = z.number().int().min(1).max(API_KEY_RATE_MAX).nullable();

/**
 * `PATCH /api/api-keys/limits` — superadmin, audited, a reason required.
 * `limits` names only the budgets being changed; `null` is "no limit" for that
 * budget (send the suggested number to go back to it). `rate` sets the burst
 * rule; null in either half switches the rule off.
 */
export const updateApiKeyLimitsSchema = z
  .object({
    mode: z.enum(API_KEY_LIMIT_MODES).optional(),
    limits: z.record(z.enum(API_KEY_LIMIT_BUDGETS), limitValue).optional(),
    rate: z.object({ perSecond: rateValue, burst: rateValue }).strict().optional(),
    reason: z.string().trim().min(3, 'Say why the limits are changing.').max(500),
  })
  .strict()
  .refine(
    (v) => !v.rate || v.rate.perSecond === null || v.rate.burst === null || v.rate.burst >= v.rate.perSecond,
    { message: 'The burst is how many calls may arrive at once, so it cannot be below the per-second rate.', path: ['rate', 'burst'] },
  );
export type UpdateApiKeyLimitsInput = z.infer<typeof updateApiKeyLimitsSchema>;

export const apiKeyLimitRowSchema = z.object({
  id: z.enum(API_KEY_LIMIT_BUDGETS),
  label: z.string(),
  kind: z.enum(['general', 'cost', 'method']),
  scope: z.enum(['org', 'olt']),
  covers: z.string(),
  measured: z.string(),
  why: z.string(),
  window: z.enum(['minute', 'tenMinutes', 'hour']),
  suggested: z.number().int().nullable(),
  smartolt: z.string().nullable(),
  /** In force (or counted against, when observing). Null = no separate limit. */
  limit: z.number().int().nullable(),
  /** `default` = the suggestion, nobody has set it; `operator` = chosen. */
  source: z.enum(['default', 'operator']),
  /** The fullest budget in the CURRENT window among those the reader can see: an org's, or one OLT's. */
  busiestNow: z.number().int(),
  /** Whose that is — the organisation, or the OLT by name. */
  busiestLabel: z.string().nullable(),
  /** The key that has made the most of those calls in the current window. */
  topKey: z.object({ name: z.string(), count: z.number().int() }).nullable(),
  /** The fullest any budget got in one window since counting began. */
  peak: z.number().int(),
  /** Calls over the limit since counting began — refused when enforcing, only counted when observing. */
  over: z.number().int(),
  /**
   * What calls of this kind have actually taken on this server since the API
   * started — every such call, people's included — so a budget can follow the
   * platform as its paths get faster. Null for the budgets that are not a cost.
   */
  timing: z.object({ p50Ms: z.number(), p95Ms: z.number(), samples: z.number().int() }).nullable(),
});
export type ApiKeyLimitRow = z.infer<typeof apiKeyLimitRowSchema>;

/** `GET /api/api-keys/limits`. */
export const apiKeyLimitsStateSchema = z.object({
  mode: z.enum(API_KEY_LIMIT_MODES),
  modeSource: z.enum(['default', 'operator']),
  budgets: z.array(apiKeyLimitRowSchema),
  rate: z.object({
    perSecond: z.number().int().nullable(),
    burst: z.number().int().nullable(),
    suggested: z.object({ perSecond: z.number().int(), burst: z.number().int() }),
    source: z.enum(['default', 'operator']),
    /** Calls over the burst rule since counting began. */
    over: z.number().int(),
  }),
  /** When the counters started — they live in the API process and restart with it. */
  countingSince: z.number().int(),
  /** False when the stored setting could not be read and the defaults are in force. */
  settingReadable: z.boolean(),
  updatedAt: z.number().int().nullable(),
  updatedBy: z.string().nullable(),
});
export type ApiKeyLimitsState = z.infer<typeof apiKeyLimitsStateSchema>;
